<template>
  <transition name="image-preview-fade">
    <div
      v-if="visible"
      class="image-preview-mask"
      @click.self="close"
      @wheel.prevent="handleWheel"
    >
      <button
        type="button"
        class="image-preview-close"
        aria-label="关闭"
        @click="close"
      >
        ×
      </button>

      <button
        v-if="showPrev"
        type="button"
        class="image-preview-nav image-preview-prev"
        aria-label="上一张"
        @click.stop="prev"
      >
        ‹
      </button>

      <div
        ref="body"
        class="image-preview-body"
        @click.stop
        @mousedown="handleMouseDown"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @touchcancel="handleTouchEnd"
        @dblclick.prevent="handleDoubleClick"
      >
        <img
          :key="currentUrl"
          ref="img"
          :src="currentUrl"
          class="image-preview-img"
          :class="{
            'is-zoomed': scale > 1,
            'is-interacting': dragging || pinching
          }"
          :style="imageStyle"
          alt="图片预览"
          draggable="false"
          @click.stop
          @dragstart.prevent
        >
      </div>

      <button
        v-if="showNext"
        type="button"
        class="image-preview-nav image-preview-next"
        aria-label="下一张"
        @click.stop="next"
      >
        ›
      </button>

      <div class="image-preview-toolbar" @click.stop>
        <button
          type="button"
          class="image-preview-zoom-btn"
          aria-label="缩小"
          :disabled="scale <= minScale"
          @click="zoomOut"
        >
          −
        </button>
        <span class="image-preview-zoom-value">{{ zoomPercent }}%</span>
        <button
          type="button"
          class="image-preview-zoom-btn"
          aria-label="放大"
          :disabled="scale >= maxScale"
          @click="zoomIn"
        >
          +
        </button>
        <button
          v-if="scale !== 1 || translateX !== 0 || translateY !== 0"
          type="button"
          class="image-preview-zoom-btn image-preview-reset-btn"
          aria-label="重置"
          @click="resetTransform"
        >
          1:1
        </button>
      </div>

      <div v-if="urls.length > 1" class="image-preview-counter">
        {{ currentIndex + 1 }} / {{ urls.length }}
      </div>
    </div>
  </transition>
</template>

<script>
const MIN_SCALE = 1
const MAX_SCALE = 4
const ZOOM_STEP = 0.25
const DOUBLE_TAP_DELAY = 300

export default {
  data() {
    return {
      visible: false,
      urls: [],
      currentIndex: 0,
      resolve: null,
      scale: 1,
      translateX: 0,
      translateY: 0,
      minScale: MIN_SCALE,
      maxScale: MAX_SCALE,
      dragging: false,
      pinching: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOriginX: 0,
      dragOriginY: 0,
      pinchStartDistance: 0,
      pinchStartScale: 1,
      lastTapTime: 0,
      lastTapX: 0,
      lastTapY: 0
    }
  },

  computed: {
    currentUrl() {
      return this.urls[this.currentIndex] || ''
    },
    showPrev() {
      return this.urls.length > 1 && this.currentIndex > 0
    },
    showNext() {
      return this.urls.length > 1 && this.currentIndex < this.urls.length - 1
    },
    zoomPercent() {
      return Math.round(this.scale * 100)
    },
    imageStyle() {
      return {
        transform: `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`
      }
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.unbindDragListeners()
    this.unlockScroll()
  },

  methods: {
    open(options = {}) {
      const normalized = this.normalizeOptions(options)
      if (!normalized.urls.length) {
        return Promise.resolve(false)
      }

      this.urls = normalized.urls
      this.currentIndex = normalized.index
      this.resetTransform()
      this.visible = true
      this.lockScroll()

      return new Promise(resolve => {
        this.resolve = resolve
      })
    },

    normalizeOptions(options) {
      if (typeof options === 'string') {
        return { urls: [options], index: 0 }
      }

      const urls = Array.isArray(options.urls)
        ? options.urls.filter(Boolean)
        : options.url
          ? [options.url]
          : []

      let index = Number.isInteger(options.index) ? options.index : 0

      if (options.url && urls.length > 1) {
        const matchedIndex = urls.indexOf(options.url)
        if (matchedIndex >= 0) {
          index = matchedIndex
        }
      }

      index = Math.max(0, Math.min(index, Math.max(urls.length - 1, 0)))

      return { urls, index }
    },

    close() {
      if (!this.visible) return
      this.visible = false
      this.resetTransform()
      this.unlockScroll()
      this.resolve && this.resolve(true)
      this.resolve = null
    },

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.resetTransform()
      }
    },

    next() {
      if (this.currentIndex < this.urls.length - 1) {
        this.currentIndex += 1
        this.resetTransform()
      }
    },

    resetTransform() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
      this.dragging = false
      this.pinching = false
      this.pinchStartDistance = 0
    },

    clampScale(value) {
      return Math.min(this.maxScale, Math.max(this.minScale, value))
    },

    zoomTo(nextScale, originX, originY) {
      const prevScale = this.scale
      const scale = this.clampScale(nextScale)

      if (scale === prevScale) {
        if (scale === 1) {
          this.translateX = 0
          this.translateY = 0
        }
        return
      }

      const body = this.$refs.body
      if (!body) {
        this.scale = scale
        if (scale === 1) {
          this.translateX = 0
          this.translateY = 0
        }
        return
      }

      const rect = body.getBoundingClientRect()
      const centerX = originX == null ? rect.left + rect.width / 2 : originX
      const centerY = originY == null ? rect.top + rect.height / 2 : originY
      const offsetX = centerX - rect.left - rect.width / 2
      const offsetY = centerY - rect.top - rect.height / 2
      const ratio = scale / prevScale

      this.translateX = offsetX - (offsetX - this.translateX) * ratio
      this.translateY = offsetY - (offsetY - this.translateY) * ratio
      this.scale = scale

      if (scale === 1) {
        this.translateX = 0
        this.translateY = 0
      }
    },

    zoomIn() {
      this.zoomTo(this.scale + ZOOM_STEP)
    },

    zoomOut() {
      this.zoomTo(this.scale - ZOOM_STEP)
    },

    handleWheel(event) {
      if (!this.visible) return
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
      this.zoomTo(this.scale + delta, event.clientX, event.clientY)
    },

    handleDoubleClick(event) {
      if (this.scale > 1) {
        this.resetTransform()
      } else {
        this.zoomTo(2, event.clientX, event.clientY)
      }
    },

    handleMouseDown(event) {
      if (event.button !== 0 || this.scale <= 1) return
      event.preventDefault()
      this.dragging = true
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.dragOriginX = this.translateX
      this.dragOriginY = this.translateY
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
    },

    handleMouseMove(event) {
      if (!this.dragging) return
      this.translateX = this.dragOriginX + (event.clientX - this.dragStartX)
      this.translateY = this.dragOriginY + (event.clientY - this.dragStartY)
    },

    handleMouseUp() {
      this.dragging = false
      this.unbindDragListeners()
    },

    unbindDragListeners() {
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)
    },

    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },

    getTouchCenter(touches) {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      }
    },

    handleTouchStart(event) {
      const touches = event.touches

      if (touches.length === 2) {
        event.preventDefault()
        this.pinching = true
        this.dragging = false
        this.pinchStartDistance = this.getTouchDistance(touches)
        this.pinchStartScale = this.scale
        this.lastTapTime = 0
        return
      }

      if (touches.length === 1) {
        const touch = touches[0]
        const now = Date.now()
        const isDoubleTap =
          now - this.lastTapTime < DOUBLE_TAP_DELAY &&
          Math.abs(touch.clientX - this.lastTapX) < 30 &&
          Math.abs(touch.clientY - this.lastTapY) < 30

        if (isDoubleTap) {
          event.preventDefault()
          if (this.scale > 1) {
            this.resetTransform()
          } else {
            this.zoomTo(2, touch.clientX, touch.clientY)
          }
          this.lastTapTime = 0
          return
        }

        this.lastTapTime = now
        this.lastTapX = touch.clientX
        this.lastTapY = touch.clientY

        if (this.scale > 1) {
          this.dragging = true
          this.dragStartX = touch.clientX
          this.dragStartY = touch.clientY
          this.dragOriginX = this.translateX
          this.dragOriginY = this.translateY
        }
      }
    },

    handleTouchMove(event) {
      const touches = event.touches

      if (touches.length === 2 && this.pinchStartDistance > 0) {
        event.preventDefault()
        this.pinching = true
        const distance = this.getTouchDistance(touches)
        const center = this.getTouchCenter(touches)
        const nextScale = this.pinchStartScale * (distance / this.pinchStartDistance)
        this.zoomTo(nextScale, center.x, center.y)
        return
      }

      if (touches.length === 1 && this.dragging && this.scale > 1) {
        event.preventDefault()
        const touch = touches[0]
        this.translateX = this.dragOriginX + (touch.clientX - this.dragStartX)
        this.translateY = this.dragOriginY + (touch.clientY - this.dragStartY)
      }
    },

    handleTouchEnd(event) {
      if (event.touches.length < 2) {
        this.pinching = false
        this.pinchStartDistance = 0
      }

      if (event.touches.length === 0) {
        this.dragging = false
        this.pinching = false
      } else if (event.touches.length === 1 && this.scale > 1) {
        const touch = event.touches[0]
        this.dragging = true
        this.dragStartX = touch.clientX
        this.dragStartY = touch.clientY
        this.dragOriginX = this.translateX
        this.dragOriginY = this.translateY
      }
    },

    handleKeydown(event) {
      if (!this.visible) return

      if (event.key === 'Escape') {
        this.close()
      } else if (event.key === 'ArrowLeft') {
        this.prev()
      } else if (event.key === 'ArrowRight') {
        this.next()
      } else if (event.key === '+' || event.key === '=') {
        this.zoomIn()
      } else if (event.key === '-' || event.key === '_') {
        this.zoomOut()
      } else if (event.key === '0') {
        this.resetTransform()
      }
    },

    lockScroll() {
      document.body.style.overflow = 'hidden'
    },

    unlockScroll() {
      document.body.style.overflow = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.image-preview-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 72px;
  box-sizing: border-box;
  touch-action: none;
  overflow: hidden;
}

.image-preview-body {
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
}

.image-preview-img {
  max-width: 100%;
  max-height: calc(100vh - 96px);
  width: auto;
  height: auto;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  transform-origin: center center;
  transition: transform 0.08s ease-out;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  will-change: transform;

  &.is-zoomed {
    cursor: grab;
  }

  &.is-interacting {
    cursor: grabbing;
    transition: none;
  }
}

.image-preview-close {
  position: absolute;
  top: 16px;
  right: 20px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
}

.image-preview-nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
}

.image-preview-prev {
  left: 20px;
}

.image-preview-next {
  right: 20px;
}

.image-preview-toolbar {
  position: absolute;
  bottom: 56px;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.45);
}

.image-preview-zoom-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.22);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.image-preview-reset-btn {
  width: auto;
  padding: 0 12px;
  border-radius: 18px;
  font-size: 13px;
}

.image-preview-zoom-value {
  min-width: 52px;
  color: #fff;
  font-size: 13px;
  text-align: center;
  user-select: none;
}

.image-preview-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
  padding: 6px 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 14px;
}

.image-preview-fade-enter-active,
.image-preview-fade-leave-active {
  transition: opacity 0.25s ease;
}

.image-preview-fade-enter,
.image-preview-fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 768px) {
  .image-preview-mask {
    padding: 56px 16px 96px;
  }

  .image-preview-img {
    max-height: calc(100vh - 160px);
  }

  .image-preview-nav {
    width: 36px;
    height: 36px;
    font-size: 26px;
  }

  .image-preview-prev {
    left: 8px;
  }

  .image-preview-next {
    right: 8px;
  }

  .image-preview-toolbar {
    bottom: 52px;
  }

  .image-preview-counter {
    bottom: 16px;
  }
}
</style>
